import React, { useState } from "react";
import CallIconAnimated from "svgs/CallIconAnimated";

import CallIndicator from "./CallIndicator";
import PeopleIcon from "@mui/icons-material/People";
import TagIcon from "@mui/icons-material/LocalOffer";

import { StatusTime } from "../Tiles/ProcessTile/ProcessTile";
import {
  AnimatedHubletViewStyles,
  MINI_MODE_HEIGHT,
  widthPxFromPercent,
} from "./HubletViewStyles/HubletViewStyles";
import { AnimatedStatusIndicatorStyles } from "./HubletViewStyles/StatusIndicatorStyles";
import useContainerDimensions from "utils/useContainerDimensions";
import {
  getMinimumOfWidthOrHeight,
  getPnlTitle,
  hasUserClickedMinimodeButtonAtom,
  useIsMiniMode,
} from "./hubletViewUtils";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { IconButton, useMediaQuery } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { HubletViewBottomRowItems } from "./HubletViewBottomRowItems";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { ChevronLeft } from "@mui/icons-material";
import styled from "styled-components/macro";
import { animated, useSpring } from "@react-spring/web";
import { useMount, useWindowSize } from "react-use";
import {
  BREAKPOINT_DESKTOP_XL,
  BREAKPOINT_TABLET,
  NAV_HEIGHT_PX,
  useBreakpoints,
} from "constants/constants";
import { useAtom } from "jotai";
import { HUBLETVIEW_XL_OFFSET_TOP } from "./HubletViewStyles/HubletPageStyles";

type HubletViewContentProps = {
  isLessThanOneMinute: boolean;
  skuCodeDisplay: string;
  skuDisplay: string;
  teamSize?: number | null;
  status: string;
  isIE: boolean;
  isNegative: boolean;
  processName: string;
  ge?: number | null;
  pnlCategory: string;
  isCall: boolean;
  callTarget: string | null;
  displayedCallTime: any;
  displayedCallUnit: string;
  isCallAnimationActive: boolean;
  isGridLayout: boolean;
  isToday: boolean;
  showCountdown: boolean;
  displayedTime1: number; // first number of the status e.g. "1" in "1h2m"
  displayedUnit1: string; // first unit of the status e.g. "h" in "1h2m"
  displayedTime2: number; // second number of the status e.g. "2" in "1h2m"
  displayedUnit2: string; // second unit of the status e.g. the "m" in "1h2m"
  processId: string;
  hasResized: boolean;
};
export const HubletViewContent = React.memo<HubletViewContentProps>(
  ({
    isLessThanOneMinute,
    skuCodeDisplay,
    skuDisplay,
    teamSize,
    isToday,
    status,
    isIE,
    isNegative,
    processName,
    ge,
    pnlCategory,
    isCall,
    callTarget,
    displayedCallTime,
    displayedCallUnit,
    isCallAnimationActive,
    isGridLayout,
    showCountdown,
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    processId,
    hasResized,
  }) => {
    const [ref, dimensions] = useContainerDimensions();
    const minOfWidthOrHeight = getMinimumOfWidthOrHeight(dimensions);
    const width = dimensions.width || 0;
    const { isDesktopXlOrLarger } = useBreakpoints();
    const height = (dimensions.height || 0) - (isDesktopXlOrLarger ? 32 : 0);
    const ckey = useCustomerKey();
    const isPrint = useMediaQuery("print");
    const { translate } = useFlightTranslation();
    const [
      {
        hubletView: { hideSku },
      },
    ] = useFlightConfig();
    const [isMiniMode] = useIsMiniMode();
    const springHeight = useHubletViewSpringHeight({
      width,
      hasResized,
      isGridLayout,
    });
    const springScale = useSpring({
      transform: `scale(${isMiniMode ? 0.5 : 1}) translateY(${
        isMiniMode ? -36 : 0
      }px)`,
    });
    return (
      <AnimatedHubletViewStyles
        {...{
          style: springHeight,
          $ckey: ckey,
          ref,
          $width: width,
          $height: height,
          $minOfWidthOrHeight: minOfWidthOrHeight,
          $isGridLayout: isGridLayout,
          $skuCodeDisplayLength: skuCodeDisplay.length,
          className: `details-process details-process-${pnlCategory}${
            isNegative ? " negative" : ""
          }`,
          $isMiniMode: isMiniMode,
        }}
        data-component-name={"Hublet View"}
      >
        <div
          className={`background background-${pnlCategory}`}
          data-server-names={[SERVER_NAMES.askrSegments]}
        />
        <animated.div
          style={springScale}
          className={`details-top-row${
            !isToday || isPrint ? " outOfFocus" : ""
          }`}
        >
          <div
            className="sku-indicator top-row-item"
            {...(hideSku ? { style: { visibility: "hidden" } } : {})}
            data-server-names={[SERVER_NAMES.midgardLastDataDumpRow]}
          >
            {Boolean(skuCodeDisplay) && (
              <>
                <div className="sku-indicator-label top-row-label">
                  {ckey === "ivc" ? "FG" : "SKU"}
                </div>
                <div className="sku-indicator-content top-row-value">
                  <div className="sku-indicator-sku-code">{skuCodeDisplay}</div>
                  <div className="sku-indicator-sku-display">{skuDisplay}</div>
                </div>
              </>
            )}
          </div>
          {teamSize ? (
            <div
              className="workers-indicator top-row-item"
              data-server-names={[SERVER_NAMES.midgardLastDataDumpRow]}
            >
              <div className="workers-icon top-row-icon">
                <PeopleIcon />
              </div>
              <div className="workers-status top-row-value">{teamSize}</div>
            </div>
          ) : null}
          <div className="tag-indicator top-row-item">
            <div className="tag-icon top-row-icon">
              <TagIcon />
            </div>
            <div
              className="tag-status top-row-value"
              data-server-names={[SERVER_NAMES.askrSegments]}
            >
              {status || translate("no_tag").toUpperCase()}
            </div>
          </div>
        </animated.div>

        <div
          className={`details-call-and-status-container${
            isIE ? " internetExplorer" : ""
          }${!isToday || isPrint ? " outOfFocus" : ""}`}
        >
          {isCall && (
            <div
              className="details-call-icon"
              data-server-names={[SERVER_NAMES.midgardDataDumpHubletView]}
            >
              <CallIconAnimated isCallAnimationActive={isCallAnimationActive} />
            </div>
          )}
          <Status
            processName={processName}
            ge={ge}
            pnlCategory={pnlCategory}
            isGridLayout={isGridLayout}
            showCountdown={showCountdown}
            isLessThanOneMinute={isLessThanOneMinute}
            displayedTime1={displayedTime1}
            displayedUnit1={displayedUnit1}
            displayedTime2={displayedTime2}
            displayedUnit2={displayedUnit2}
            processId={processId}
            containerHeight={dimensions.height || 0}
          />
        </div>

        {isCall ? (
          <div className="details-call-indicator-spacing">
            <CallIndicator
              callTarget={callTarget}
              callDisplayedDuration={displayedCallTime}
              callDisplayedDurationUnits={displayedCallUnit}
            />
          </div>
        ) : (
          <HubletViewBottomRowItems
            isGridLayout={isGridLayout}
            minOfWidthOrHeight={minOfWidthOrHeight}
            width={width}
          />
        )}
        <MiniModeButton />
      </AnimatedHubletViewStyles>
    );
  }
);

function useHubletViewSpringHeight({
  isGridLayout,
  width,
  hasResized,
}: {
  isGridLayout: boolean;
  width: number;
  hasResized: boolean;
}) {
  const { height: innerHeight } = useWindowSize();
  const [isMiniMode] = useIsMiniMode();
  const { isTabletOrLarger, isDesktopOrLarger, isDesktopXlOrLarger } =
    useBreakpoints();
  const isDesktop2XlOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP_XL + 1000}px)`
  );
  let hubletViewHeight = isMiniMode
    ? `${MINI_MODE_HEIGHT}px`
    : isGridLayout
      ? `${innerHeight}px`
      : widthPxFromPercent(width, 77) + "px";
  if (isTabletOrLarger && !isDesktopOrLarger) {
    hubletViewHeight = isMiniMode
      ? `${MINI_MODE_HEIGHT}px`
      : widthPxFromPercent(width, 77) + "px";
  }
  if (isDesktopOrLarger) {
    hubletViewHeight = isMiniMode
      ? `${MINI_MODE_HEIGHT}px`
      : isGridLayout
        ? `${innerHeight}px`
        : `${innerHeight - NAV_HEIGHT_PX}px`;
  }
  if (isDesktopXlOrLarger) {
    hubletViewHeight = isMiniMode
      ? `${MINI_MODE_HEIGHT + 3 * HUBLETVIEW_XL_OFFSET_TOP}px`
      : isGridLayout
        ? `${innerHeight}px`
        : `${innerHeight - NAV_HEIGHT_PX - HUBLETVIEW_XL_OFFSET_TOP}px`;
  }
  if (isDesktop2XlOrLarger) {
    hubletViewHeight = isMiniMode
      ? `${MINI_MODE_HEIGHT + 7 * HUBLETVIEW_XL_OFFSET_TOP}px`
      : isGridLayout
        ? `${innerHeight}px`
        : `${innerHeight - NAV_HEIGHT_PX - HUBLETVIEW_XL_OFFSET_TOP}px`;
  }
  const [isMounted, setisMounted] = useState(false);
  const springHeight = useSpring({
    height: hubletViewHeight,
    immediate: hasResized || !isMounted,
  });
  useMount(() => {
    setTimeout(() => {
      setisMounted(true);
    }, 500);
  });
  return springHeight;
}

function MiniModeButton() {
  const [isMiniMode, setIsMiniMode] = useIsMiniMode();
  const [hasUserClickedButton, setHasUserClickedButton] = useAtom(
    hasUserClickedMinimodeButtonAtom
  );
  const { isDesktopOrLarger } = useBreakpoints();
  return (
    <MiniModeButtonStyles
      onClick={() => {
        setIsMiniMode(prev => {
          const next = !prev;
          if (next && isDesktopOrLarger) {
            // instantly scroll HubletView right side to top, since pnl chart will be changing position & user might be scrolled down on right side
            const hubletViewRightSide = document.querySelector<HTMLElement>(
              ".datepickerAndCharts"
            );
            hubletViewRightSide?.scrollTo({ top: 0 });
          }
          return next;
        });
        if (!hasUserClickedButton) {
          setHasUserClickedButton(true);
        }
      }}
    >
      <IconButton className="mini-mode-button">
        <ChevronLeft
          style={{
            transform: `rotate(${isMiniMode ? -90 : 90}deg)`,
          }}
        />
      </IconButton>
    </MiniModeButtonStyles>
  );
}
const MiniModeButtonStyles = styled.div`
  display: none;
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    display: flex;
  }
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.05);
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  .MuiIconButton-root {
    svg {
      transition: transform 0.3s;
    }
  }
  .mini-mode-button {
    pointer-events: none;
  }
  @media (min-width: ${BREAKPOINT_DESKTOP_XL}px) {
    height: 64px;
    .MuiIconButton-root {
      .MuiSvgIcon-root {
        width: 3em;
        height: 3em;
      }
    }
  }
`;

const Status = React.memo<{
  isLessThanOneMinute: boolean;
  isGridLayout: boolean;
  processName: string;
  showCountdown: boolean;
  ge?: number | null;
  pnlCategory: string;
  displayedTime1: number; // first number of the status e.g. "1" in "1h2m"
  displayedUnit1: string; // first unit of the status e.g. "h" in "1h2m"
  displayedTime2: number; // second number of the status e.g. "2" in "1h2m"
  displayedUnit2: string; // second unit of the status e.g. the "m" in "1h2m"
  processId: string;
  containerHeight: number;
}>(
  ({
    isLessThanOneMinute,
    isGridLayout,
    processName,
    showCountdown,
    ge,
    pnlCategory,
    displayedTime1,
    displayedUnit1,
    displayedTime2,
    displayedUnit2,
    processId,
    containerHeight,
  }) => {
    const { translate } = useFlightTranslation();
    const [ref, dimensions] = useContainerDimensions();
    const width = dimensions.width || 0;
    const widthForScaling = isGridLayout
      ? Math.min(containerHeight, width)
      : width;
    const ckey = useCustomerKey();
    const [isMiniMode] = useIsMiniMode();
    const windowSize = useWindowSize();
    // 🐱‍💻
    const translateY =
      windowSize.width > 2000
        ? 40
        : windowSize.width > 1200
          ? -0
          : windowSize.width > 800
            ? -70
            : -70;
    const springScale = useSpring({
      transform: `scale(${isMiniMode ? 0.2 : 1}) translateY(${
        isMiniMode ? translateY : 0
      }%)`,
    });
    return (
      <AnimatedStatusIndicatorStyles
        style={springScale}
        ref={ref}
        $width={widthForScaling}
        $isGridLayout={isGridLayout}
        data-component-name={"Hublet View Status Indicator"}
      >
        <div
          className="status-indicator-machine-name"
          data-server-names={[SERVER_NAMES.midgardProcessGroups]}
        >
          {processName}
        </div>
        <StatusTime
          ge={ge}
          pnlCategory={pnlCategory}
          isLessThanOneMinute={isLessThanOneMinute}
          displayedTime1={displayedTime1}
          displayedUnit1={displayedUnit1}
          displayedTime2={displayedTime2}
          displayedUnit2={displayedUnit2}
          containerWidth={widthForScaling}
        />
        <div
          className="status-indicator-tag-name"
          data-server-names={[SERVER_NAMES.askrSegments]}
        >
          {showCountdown && pnlCategory === "producing"
            ? translate("remaining")
            : getPnlTitle(pnlCategory, translate, ckey, processId)}
        </div>
      </AnimatedStatusIndicatorStyles>
    );
  }
);
